import { NuggetUXVariant } from '../molecules/Nugget/NuggetUITypes';

type DictionaryTerm =
  | 'rigorous major'
  | 'rigorous, competitive major'
  | 'reputable state university'
  | 'Standard 4 year college'
  | 'Selective college+'
  | 'higher performing community colleges'
  | 'high quality trade schools'
  | 'top-tier university'
  | 'very selective university'
  | 'Very strong extracurricular profile'
  | 'Exceptional extracurricular profile'
  | 'school clubs, community service, and job-related activities'
  | 'clubs, skills competitions and community service projects'
  | string;

export type DictionaryValues = {
  title?: string;
  description: string;
}[];

export type DisplayListType =
  | { type: 'bullet' }
  | { type: 'number' }
  | { type: 'block' }
  | {
      type: 'nugget';
      panelNuggetVariant: NuggetUXVariant;
      onClickUrl: string;
    };

type DictionaryContent = Readonly<{
  title?: string;
  values: DictionaryValues;
  displayType: DisplayListType;
}>;

export type DictionaryRecord = Record<DictionaryTerm, DictionaryContent>;

/* SHARED DICTIONARY CONTENT
 *
 * These dictionary entries are shared across multiple terms. We define them once here and then use them in the record below.
 */
const SHARED_PROFILE_FOR_SELECTED_UNIVERSITY: DictionaryContent = {
  title:
    'Very selective universities value well-rounded applicants who demonstrate sustained engagement and responsibility, even if achievements are more regional or local in scope.',
  displayType: { type: 'number' },
  values: [
    {
      title: 'Club Leadership:',
      description:
        'Serving as president or officer of a school club (e.g., Key Club, environmental club, robotics team) demonstrates leadership and commitment over time.',
    },
    {
      title: 'Regional Academic or Artistic Achievements:',
      description:
        'Participating in and earning recognition in events like regional science fairs, music competitions, or writing contests shows talent and dedication.',
    },
    {
      title: 'Volunteer Work with Impact:',
      description:
        'Logging substantial hours with a clear focus, such as tutoring underprivileged students or organizing community cleanups, highlights responsibility and service.',
    },
    {
      title: 'Varsity Sports Participation:',
      description:
        'Being a consistent member of a varsity team, even without state-level recognition, reflects discipline, teamwork, and time management.',
    },
    {
      title: 'Part-Time Work or Family Responsibilities:',
      description:
        'Managing a job or helping with family responsibilities while maintaining strong academics shows maturity, work ethic, and resilience.',
    },
  ],
} as const;

const SHARED_STRONG_EXTRACTION_PROFILE: DictionaryContent = {
  title:
    'Top-tier universities value activities that show sustained effort, leadership, and tangible accomplishments. Examples include:',
  displayType: { type: 'number' },
  values: [
    {
      title: 'Student Government Leadership:',
      description:
        'Serving as student body president, class officer, or on the student council demonstrates leadership, initiative, and organizational skills.',
    },
    {
      title: 'Academic Competitions:',
      description:
        'Excelling in national or international contests like Science Olympiad, MathCounts, Model UN, or debate tournaments showcases intellectual ability and dedication.',
    },
    {
      title: 'Community Service Projects:',
      description:
        'Founding or leading impactful initiatives, such as organizing a local food drive or creating a nonprofit, reflects commitment to making a difference.',
    },
    {
      title: 'Athletic Team Captain or State/National Competitor:',
      description:
        'Leadership in sports or achieving high-level recognition (e.g., All-State, Nationals) highlights teamwork, discipline, and perseverance.',
    },
    {
      title: 'Research or Internship Experience:',
      description:
        'Conducting independent research under a mentor or interning in a field of interest demonstrates academic passion and real-world application.',
    },
  ],
} as const;

const SHARED_RIGOROUS_MAJOR: DictionaryContent = {
  displayType: { type: 'block' },
  values: [
    {
      description:
        'A rigorous college major demands a high level of intellectual engagement, time commitment, and critical thinking. These majors typically feature challenging coursework, extensive reading or problem-solving, a fast-paced curriculum, and significant hands-on work such as labs, research projects, or internships. Examples often include majors in STEM (e.g., engineering, biochemistry), pre-professional tracks (e.g., pre-med, architecture), and fields with high quantitative or analytical demands (e.g., economics, data science). Students in rigorous majors usually spend 15-25 hours per week on coursework outside of class and may require maintaining a higher GPA to remain in the program or qualify for advanced opportunities.',
    },
  ],
} as const;

export const DATA_DICTIONARY: DictionaryRecord = {
  'rigorous major': SHARED_RIGOROUS_MAJOR,
  'rigorous, competitive major': SHARED_RIGOROUS_MAJOR,
  'Standard 4 year college': {
    displayType: { type: 'block' },
    values: [
      {
        description:
          'Typically accepts 50-80% of applicants, providing accessible undergraduate education to a broad range of students. These schools often have student-to-faculty ratios around 16:1 and tuition costs ranging from $10,000 to $30,000 per year for in-state public colleges.',
      },
    ],
  },
  'Selective college+': {
    displayType: { type: 'block' },
    values: [
      {
        description:
          'Admit less than 40% of applicants, typically requiring exceptional academic performance, extracurricular achievements, and personal qualities. Students score well above average on standardized tests (e.g., SAT minimum of 1250, ACT minimum of 28). Tuition typically exceeds $40,000 per year (before scholarships and aid), reflecting their academic rigor and reputation for excellence and opportunities for networking and leadership development',
      },
    ],
  },
  'reputable state university': {
    displayType: { type: 'bullet' },
    values: [
      { description: '6 year graduation rate of 60%+' },
      { description: 'Job placement of at 80%+' },
      { description: '10 year ROI of at least $200,000' },
    ],
  },
  'higher performing community colleges': {
    displayType: { type: 'bullet' },
    values: [
      { description: 'Graduation or transfer rates above 40%' },
      { description: 'Student-to-Faculty Ratio below 18:1' },
      { description: 'Job placement rates for career-focused programs >75%' },
      { description: 'Strong articulation agreements with four-year universities' },
      { description: 'Robust academic advising, tutoring, and career counseling' },
    ],
  },
  'high quality trade schools': {
    displayType: { type: 'bullet' },
    values: [
      { description: 'Completion rates >60%' },
      { description: 'Job placement rates of >75%' },
      { description: 'Graduates earn starting salaries between $35,000 and $60,000 at minimum' },
      {
        description:
          'Award industry-recognized certifications or licenses (e.g., HVAC, welding, medical assisting)',
      },
      { description: 'Modern facilities and equipment that mirror workplace settings' },
    ],
  },
  'top-tier university': {
    displayType: { type: 'block' },
    values: [
      {
        description:
          'A top-tier university is a highly prestigious institution recognized for its rigorous academics, groundbreaking research, and influential alumni. They provide access to world-class education, influential networks, and career opportunities that can significantly enhance lifelong success. These schools typically admit fewer than 15% of applicants, with incoming students often scoring in the top 5% on standardized tests (e.g., SAT scores above 1450 or ACT scores above 33). Tuition often exceeds $60,000 annually, though significant financial aid is available for qualifying students.',
      },
    ],
  },
  'very selective university': {
    displayType: { type: 'block' },
    values: [
      {
        description:
          'A very selective university often admits less than 20% of applicants, with incoming students frequently scoring in the top 10% on standardized tests like the SAT (1400+ median score) or ACT (30+ median score). These institutions boast small student-to-faculty ratios, often around 8:1, and tuition can exceed $50,000 per year, reflecting their focus on academic rigor and exclusivity.',
      },
    ],
  },
  'Very strong extracurricular profile': SHARED_STRONG_EXTRACTION_PROFILE,
  'Exceptional extracurricular profile': SHARED_STRONG_EXTRACTION_PROFILE,
  'school clubs, community service, and job-related activities':
    SHARED_PROFILE_FOR_SELECTED_UNIVERSITY,
  'clubs, skills competitions and community service projects':
    SHARED_PROFILE_FOR_SELECTED_UNIVERSITY,
};
